import { Heading } from '@chakra-ui/layout'
import { Box, Button } from '@chakra-ui/react'
import { Layout } from 'components/atoms'
import { InputBuild, SelectBuild } from 'components/molecules'
import { VehicleAutocomplete } from 'components/molecules/VehicleAutocomplete'
import { useContractContext } from 'contexts/contract'
import { disableUfFields } from 'pages/Contracts/utils/disableUfFields'
import React, { useEffect, useMemo } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'

import { FieldError } from 'react-hook-form'
import isDisableToEditContract from '../../StepsForm/Utils/isDisableToEditContract'
import { StepsFormProps } from '../../StepsForm/interface'
import { FormVehicleValue } from './form'
import { FormVehicleProps } from './interfaces'


const createEmptyVehicle = (defaults: Partial<FormVehicleProps> = {}): any => ({
  chassi: '',
  renavam: '',
  uf: '',
  ufGravame: '',
  placa: '',
  anoFabricacao: undefined,
  anoModelo: undefined,
  identificacaoRemarcacao: '0',
  numeroGravame: '',
  especie: undefined,
  cor: '',
  marca: '',
  indicativoVeiculoNovo: 'not_informed',
  numeroMotor: '',
  ...defaults,
})

const DividerThick: React.FC = () => (
  <Layout type="container" full>
    <Box w="100%" borderTop="3px solid rgba(0,0,0,0.18)" my={5} />
  </Layout>
)

const FormVehicle: React.FC = () => {
  const {
    register,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useFormContext<StepsFormProps>()

  const { typeContractBoolean } = useContractContext()

  const ufContrato = watch('contract.ufContrato')
  const allowMultipleVehicles = !typeContractBoolean && ufContrato === 'PR'

  const ufForRules = watch('vehicles.ufGravame')
  const form = useMemo(() => FormVehicleValue(ufForRules), [ufForRules])

  const { fields: additionalVehicles, append, remove } = useFieldArray({
    control,
    name: 'vehiclesList',
  })

  const setDefaultValueIndicativoVeiculoNovo = (path: string) => {
    const current = watch(path as never)
    if (current === undefined || current === null) {
      setValue(path as never, 'not_informed' as never)
    }
  }

  useEffect(() => {
    setDefaultValueIndicativoVeiculoNovo('vehicles.indicativoVeiculoNovo')
  }, [])

  useEffect(() => {
    if (!allowMultipleVehicles && (additionalVehicles?.length ?? 0) > 0) {
      setValue('vehiclesList', [])
    }
  }, [allowMultipleVehicles])

  const hasError = (path: string): FieldError | undefined => {
    const keys = path.split('.')
    let current: unknown = errors as unknown

    for (const key of keys) {
      if (!current || typeof current !== 'object') return undefined
      current = (current as Record<string, unknown>)[key]
    }

    if (current && typeof current === 'object' && 'message' in current) {
      return current as FieldError
    }

    return undefined
  }

  
  const readValue = (name: string) => {
    const v = watch(name as never)
    return v == null ? '' : String(v)
  }

  const getError = (path: string): FieldError | undefined => {
    const parts = path.split('.')
    let cur: unknown = errors

    for (const p of parts) {
      if (!cur || typeof cur !== 'object') return undefined
      cur = (cur as Record<string, unknown>)[p]
    }

    return cur as FieldError | undefined
  }

  const renderVehicleFields = (
    basePath: 'vehicles' | `vehiclesList.${number}`,
    sectionForDisable: 'vehicles' | null
  ) => {
    return (
      <Layout type="row" gutter={{ y: 1.5 }} data-testid="form-fields">
        {form.map(({ layout, input, select, autocomplete }, key) => (
          <Layout {...layout} key={key}>
            {select ? (
              <SelectBuild
                {...select}
                err={getError(`${basePath}.${select.id}`)}
                {...register(`${basePath}.${select.id}` as never)}
                isDisable={
                  sectionForDisable
                    ? Boolean(disableUfFields(typeContractBoolean, select, sectionForDisable))
                    : Boolean(select.isDisable)
                }
              />
            ) : input ? (
          <InputBuild
            {...input}
            err={getError(`${basePath}.${input.id}`)}
            {...register(`${basePath}.${input.id}` as never)}
            value={readValue(`${basePath}.${input.id}`)}
            isDisable={Boolean(
              sectionForDisable &&
                typeContractBoolean &&
                isDisableToEditContract[`${sectionForDisable}.${input.id}`]
            )}
          />
            ) : (
              autocomplete && (
                <VehicleAutocomplete
                  label={autocomplete.label}
                  required={autocomplete.required}
                  name={`${basePath}.${autocomplete.id}`}
                  err={hasError(`${basePath}.${autocomplete.id}`)}
                  isDisable={Boolean(
                    sectionForDisable &&
                      typeContractBoolean &&
                      isDisableToEditContract[`${sectionForDisable}.${autocomplete.id}`]
                  )}
                />
              )
            )}
          </Layout>
        ))}
      </Layout>
    )
  }

  const handleAddVehicle = () => {
    const defaults = {
      uf: watch('vehicles.uf') || ufContrato || '',
      ufGravame: watch('vehicles.ufGravame') || ufContrato || '',
    }
    append(createEmptyVehicle(defaults))

    const newIndex = additionalVehicles.length
    setDefaultValueIndicativoVeiculoNovo(`vehiclesList.${newIndex}.indicativoVeiculoNovo`)
  }

  return (
    <Layout full type="container" px={8} display="grid" gap={6} data-testid="form-container">
      <Layout
        type="row"
        data-testid="form-heading"
        justifyContent="space-between"
        alignItems="center"
      >
        <Heading size="md" textAlign={{ sm: 'center' }}>
          Dados do veículo
        </Heading>

        {allowMultipleVehicles && (
          <Button onClick={handleAddVehicle} size="sm">
            + Adicionar veículo
          </Button>
        )}
      </Layout>

      {/* Veículo principal */}
      {renderVehicleFields('vehicles', 'vehicles')}

      {/* Veículos adicionais (somente PR) */}
      {allowMultipleVehicles && additionalVehicles.length > 0 && (
        <Layout type="container" full>
          <DividerThick />

          {additionalVehicles.map((field, idx) => (
            <Layout key={field.id} type="container" full display="grid" gap={4}>
              <Layout type="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Button variant="outline" size="sm" onClick={() => remove(idx)}>
                  Remover
                </Button>
              </Layout>

              {renderVehicleFields(`vehiclesList.${idx}` as any, null)}

              {idx < additionalVehicles.length - 1 && <DividerThick />}
            </Layout>
          ))}
        </Layout>
      )}
    </Layout>
  )
}

export default FormVehicle
